import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Columns3, Info, Pencil } from "lucide-react";
import { useAppDispatch } from "hooks";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { fetchBoards } from "containers/project-boards/project-boards.service";
import { BoardCard } from "containers/project-boards/types";
import { boardGradient } from "containers/project-boards/board-color";

type Props = {
  companyId: string;
  boardId: string;
  boardName: string;
  /** Open-deal count; omit to hide the count. */
  dealCount?: number;
  /**
   * If provided, selecting a board calls this instead of navigating to the
   * kanban board page (used on opportunity history).
   */
  onSelectBoard?: (boardId: string) => void;
  /** Add an "All boards" option that selects `""`. */
  allowAllBoards?: boolean;
  /** Pencil → edit board modal. Default true when a board is selected. */
  showEdit?: boolean;
};

const LIST_MAX_HEIGHT = 240;
const ALL_BOARDS_ID = "";

export default function ProjectBoardSwitcher({
  companyId,
  boardId,
  boardName,
  dealCount,
  onSelectBoard,
  allowAllBoards = false,
  showEdit,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState<BoardCard[]>([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const canEdit = showEdit ?? Boolean(boardId);

  const loadBoards = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBoards(companyId, { page: 1, pageSize: 200 });
      setBoards(Array.isArray(data.collection) ? data.collection : []);
    } catch {
      setBoards([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const currentBoard = useMemo(
    () => boards.find((b) => b.id === boardId) || null,
    [boards, boardId]
  );

  const dealLabel =
    dealCount === 1
      ? t("project_deal_count_one", {
          count: 1,
          defaultValue: "1 deal",
        })
      : t("project_deal_count_other", {
          count: dealCount ?? 0,
          defaultValue: "{{count}} deals",
        });

  const openEdit = () => {
    if (!boardId) return;
    setOpen(false);
    dispatch(setModalxComponent("ProjectBoardForm"));
    dispatch(setModalxComponentProps({ id: boardId }));
  };

  const selectBoard = (id: string) => {
    setOpen(false);
    if (id === boardId) return;
    if (onSelectBoard) {
      onSelectBoard(id);
      return;
    }
    if (!id) return;
    navigate(`/app/${companyId}/project-boards/${id}`);
  };

  const displayName =
    boardName ||
    (allowAllBoards && !boardId
      ? t("project_opportunity_history_all_boards", {
          defaultValue: "All boards",
        })
      : t("project_board", { defaultValue: "Board" }));

  const swatchStyle = boardId
    ? {
        background: boardGradient(
          currentBoard || { id: boardId, color: null }
        ),
      }
    : {
        background:
          "linear-gradient(135deg, var(--p-color-bg-fill-secondary) 0%, var(--p-color-bg-fill-tertiary, #94a3b8) 100%)",
      };

  return (
    <div className="flex shrink-0 items-center gap-3" ref={rootRef}>
      {dealCount !== undefined && (
        <div className="flex shrink-0 items-center gap-1.5 text-sm text-[var(--p-color-text-secondary)]">
          <span className="font-medium tabular-nums text-[var(--p-color-text)]">
            {dealLabel}
          </span>
          <span
            title={t("project_deal_count_hint", {
              defaultValue:
                "Open cards currently on this board (after filters).",
            })}
            className="inline-flex text-[var(--p-color-text-secondary)]"
          >
            <Info size={14} strokeWidth={1.75} />
          </span>
        </div>
      )}

      <div className="relative">
        <div className="flex min-w-0 items-stretch overflow-hidden rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] shadow-sm">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`flex min-w-0 max-w-[14rem] items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-[var(--p-color-bg-surface-secondary)] ${
              open ? "bg-[var(--p-color-bg-surface-secondary)]" : ""
            }`}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            <span
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-white"
              style={swatchStyle}
            >
              <Columns3 size={12} strokeWidth={2} />
            </span>
            <span className="min-w-0 truncate text-sm font-semibold text-[var(--p-color-text)]">
              {displayName}
            </span>
            <ChevronDown
              size={14}
              className={`shrink-0 text-[var(--p-color-text-secondary)] transition-transform duration-150 ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>

          {canEdit && (
            <>
              <span
                className="w-px self-stretch bg-[var(--p-color-border)]"
                aria-hidden
              />
              <button
                type="button"
                onClick={openEdit}
                title={t("edit_board", { defaultValue: "Edit board" })}
                aria-label={t("edit_board", { defaultValue: "Edit board" })}
                className="flex shrink-0 items-center px-2.5 text-[var(--p-color-text-secondary)] transition-colors hover:bg-[var(--p-color-bg-surface-secondary)] hover:text-[var(--p-color-text)]"
              >
                <Pencil size={14} strokeWidth={1.75} />
              </button>
            </>
          )}
        </div>

        {open && (
          <div
            className="absolute left-0 top-full z-50 mt-1.5 w-[min(16rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] shadow-lg"
            role="listbox"
            aria-label={t("project_boards", { defaultValue: "Boards" })}
          >
            <ul
              className="overflow-y-auto py-1"
              style={{ maxHeight: LIST_MAX_HEIGHT }}
            >
              {loading && boards.length === 0 && (
                <li className="px-3 py-3 text-center text-sm text-[var(--p-color-text-secondary)]">
                  {t("loading", { defaultValue: "Loading…" })}
                </li>
              )}
              {!loading && boards.length === 0 && !allowAllBoards && (
                <li className="px-3 py-3 text-center text-sm text-[var(--p-color-text-secondary)]">
                  {t("project_board_none", {
                    defaultValue: "No boards found.",
                  })}
                </li>
              )}
              {allowAllBoards && (
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={!boardId}
                    onClick={() => selectBoard(ALL_BOARDS_ID)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--p-color-bg-surface-secondary)] ${
                      !boardId ? "bg-[var(--p-color-bg-surface-secondary)]" : ""
                    }`}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[var(--p-color-bg-fill-secondary)] text-[var(--p-color-text-secondary)]">
                      <Columns3 size={12} strokeWidth={2} />
                    </span>
                    <span
                      className={`min-w-0 flex-1 truncate text-[var(--p-color-text)] ${
                        !boardId ? "font-semibold" : "font-medium"
                      }`}
                    >
                      {t("project_opportunity_history_all_boards", {
                        defaultValue: "All boards",
                      })}
                    </span>
                    {!boardId && (
                      <Check
                        size={14}
                        className="shrink-0 text-[var(--p-color-text-success)]"
                        strokeWidth={2.25}
                      />
                    )}
                  </button>
                </li>
              )}
              {boards.map((board) => {
                const selected = board.id === boardId;
                return (
                  <li key={board.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => selectBoard(board.id)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--p-color-bg-surface-secondary)] ${
                        selected
                          ? "bg-[var(--p-color-bg-surface-secondary)]"
                          : ""
                      }`}
                    >
                      <span
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-white"
                        style={{ background: boardGradient(board) }}
                      >
                        <Columns3 size={12} strokeWidth={2} />
                      </span>
                      <span
                        className={`min-w-0 flex-1 truncate text-[var(--p-color-text)] ${
                          selected ? "font-semibold" : "font-medium"
                        }`}
                      >
                        {board.name}
                      </span>
                      {selected && (
                        <Check
                          size={14}
                          className="shrink-0 text-[var(--p-color-text-success)]"
                          strokeWidth={2.25}
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
